import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BaseComponent,
  WmsDialogBodyComponent,
  WmsDialogFooterComponent,
  WmsDialogHeaderComponent
} from "@wms/components";
import {TabsModule} from "ngx-bootstrap/tabs";
import { LiveWidgetSettingsService } from '@wms/app/services/liveWidgetsSettings/live-widget-settings.service';
import {ChartService} from "@wms/app/services/chartService/chart.service";
import {FormsModule} from "@angular/forms";
import {PropertiesTemplateComponent} from "@wms/properties-panel";
import {includes, isEmpty, remove, toString, toUpper} from "lodash-es";
import {isDateTimeType, runtimeEventManager} from "@wm/core";
import {TooltipModule} from "ngx-bootstrap/tooltip";
import {getClonedObject, isNumberType} from "@wms/core";
import {TypeFactoryService} from "@wms/services";
import { WmsExpressionErrorDirective } from '@wms/app/directives/wms-expression-error/wms-expression-error.directive';

declare const nv: any,d3: any;

@Component({
  selector: 'wms-chart-designer',
  standalone: true,
  imports: [CommonModule, TabsModule, FormsModule, PropertiesTemplateComponent, WmsDialogHeaderComponent, WmsDialogBodyComponent, WmsDialogFooterComponent, TooltipModule, WmsExpressionErrorDirective],
  templateUrl: './chart-designer.component.html',
  styleUrls: ['./chart-designer.component.less'],
  providers: [LiveWidgetSettingsService, ChartService]
})
export class ChartDesignerComponent extends BaseComponent implements OnInit{

  @Input() propPanelInfo: any;
  @Input() activeWidgetType: any;
  @Output() handleClose = new EventEmitter<any>();
  @ViewChild('chartElement', { static: false }) chartElementRef: ElementRef;
  liveWidgetSettingsService: LiveWidgetSettingsService;
  chartService: ChartService;
  typeService: TypeFactoryService;
  convertToString = toString;
  toUpperCase = toUpper;
  chartData: any;
  isPieType: boolean;
  xDomainValues: any;
  yDomainValues: any;
  basicProperties: string[] = ['xaxislabel', 'yaxislabel', 'xunits', 'yunits', 'xnumberformat', 'xdateformat', 'ynumberformat', 'showvalues', 'viewtype', 'areaviewtype', 'staggerlabels', 'barspacing', 'showlabels', 'reducexticks', 'offsettop', 'offsetbottom', 'offsetright', 'offsetleft', 'xaxislabeldistance', 'yaxislabeldistance', 'theme', 'labeltype', 'showlabelsoutside', 'showxdistance', 'showydistance', 'nodatamessage', 'showxaxis', 'showyaxis', 'centerlabel', 'customcolors', 'xdomain', 'ydomain', 'tooltips', 'donutratio', 'interpolation'];
  propertyValueMap: any = {};
  widgetScope: any;
  widgetProps: any;
  chart: any;
  chartId: string = '#preview-chart';
  options: any = {
    'Column'         : ['staggerlabels', 'viewtype', 'reducexticks', 'barspacing', 'xaxislabel', 'yaxislabel', 'xunits', 'yunits', 'yaxislabeldistance', 'showxaxis', 'showyaxis', 'xdomain', 'ydomain'],
    'Line'           : ['staggerlabels', 'xaxislabel', 'yaxislabel', 'xunits', 'yunits', 'yaxislabeldistance', 'showxaxis', 'showyaxis', 'highlightpoints', 'linethickness', 'interpolation'],
    'Area'           : ['staggerlabels', 'areaviewtype', 'xaxislabel', 'yaxislabel', 'xunits', 'yunits', 'yaxislabeldistance', 'showxaxis', 'showyaxis', 'highlightpoints', 'xdomain', 'ydomain', 'interpolation'],
    'Cumulative Line': ['staggerlabels', 'xaxislabel', 'yaxislabel', 'xunits', 'yunits', 'yaxislabeldistance', 'showxaxis', 'showyaxis', 'highlightpoints', 'linethickness', 'interpolation'],
    'Bar'            : ['staggerlabels', 'barspacing', 'showvalues', 'viewtype', 'xaxislabel', 'yaxislabel', 'xunits', 'yunits', 'xaxislabeldistance', 'showxaxis', 'showyaxis'],
    'Pie'            : ['showlabels', 'labeltype', 'showlabelsoutside'],
    'Donut'          : ['showlabels', 'labeltype', 'donutratio', 'showlabelsoutside', 'centerlabel'],
    'Bubble'         : ['staggerlabels', 'xaxislabel', 'yaxislabel', 'xunits', 'yunits', 'yaxislabeldistance', 'showxaxis', 'showyaxis', 'showxdistance', 'showydistance']
  };
  allOptions: string[] = ['showvalues', 'showlabels', 'viewtype', 'areaviewtype', 'staggerlabels', 'reducexticks', 'barspacing', 'labeltype', 'donutratio', 'showlabelsoutside', 'xaxislabel', 'yaxislabel', 'xunits', 'yunits', 'xaxislabeldistance', 'yaxislabeldistance', 'showxdistance', 'showydistance', 'bubblesize', 'shape', 'showxaxis', 'showyaxis', 'centerlabel', 'highlightpoints', 'linethickness', 'xdomain', 'ydomain', 'interpolation'];

  constructor() {
    super();
    this.liveWidgetSettingsService = inject(LiveWidgetSettingsService);
    this.chartService = inject(ChartService);
    this.typeService = inject(TypeFactoryService);
  }

  ngOnInit() {
    this.liveWidgetSettingsService.initSettings(this.propPanelInfo, this.activeWidgetType).then(success => {
      this.initConfig();
    })
  }

  initConfig() {
    this.widgetScope = this.liveWidgetSettingsService.activeWidgetScope;
    this.widgetProps = this.widgetScope.widgetProps;
    let type = this.widgetScope.type,
      advancedPropertyGroups = this.liveWidgetSettingsService.advancedPropertyGroups;
    this.isPieType = this.chartService.isPieType(type);
    //Wait until the markup loads
    setTimeout(()=>{
      this.plotChartProxy();
    },1000);
    this.chartService.hideOrShowProperties(this.allOptions, this, false, 'showindesigner');

    if (this.projectService.isNativeMobileApplicationType()) {
      let hiddenProps = ['areaviewtype', 'staggerlabels', 'xnumberformat', 'ynumberformat'];
      hiddenProps.forEach((prop) => {
        let i = this.options[type].indexOf(prop);
        if (i !== -1) {
          this.options[type].splice(i, 1);
        }
      });
    }

    this.chartService.hideOrShowProperties(this.options[type], this, true, 'showindesigner');
    this.chartService.hideOrShowProperties(['formattype'], this, this.widgetScope.isServiceVariable, 'showindesigner');
    this.showFormattingOptions(this.widgetScope.formattype);
    this.prepareLegendPositionOptions(this.isPieType);
    //setting tab icon based on chart type
    advancedPropertyGroups[0].icon = type.toLowerCase() + 'chart';
    //X and Y Axis tab
    advancedPropertyGroups[1].show = advancedPropertyGroups[2].show = !this.isPieType;
    //Values tab
    advancedPropertyGroups[3].show = this.isPieType;
    this.propertyValueMap = this.chartService.initProperties(this.widgetScope, this.propertyValueMap);
  }

  plotChartProxy(callback?: any, position?: any) {
    this.plotChart(callback, position);
  }

  plotChart(callback?: any, position?: any) {
    let chartDataCopy: any;
    if (this.chartService.isAxisDomainValid(this.widgetScope, 'x')) {
      this.xDomainValues = { 'min' : {'x': 1},  'max' : {'x' : 5}};
    }
    if (this.chartService.isAxisDomainValid(this.widgetScope, 'y')) {
      this.yDomainValues = { 'min' : {'y' : 1}, 'max' : {'y' : 5}};
    }
    this.chartData = this.chartService.getSampleData(this.widgetScope);
    chartDataCopy = getClonedObject(this.chartData);
    //empty the svg
    // $el.find(chartId + ' svg').empty();
    this.chartElementRef.nativeElement.querySelector(this.chartId+' svg').innerHTML = '';
    nv.addGraph(() => {
      this.configureChart(chartDataCopy);
    }, () => {
      this.setPosition();
      if (callback) {
        this.chartService.modifyLegendPosition(this.widgetScope, position);
      }
    }
    );
  }

  setPosition() {
    let legendWrap = d3.select(this.chartId + ' .nv-controlsWrap .nvd3.nv-legend');
    legendWrap.attr('transform', 'translate(-65,5)'); //Default it is (0, 5)
  }

  configureChart(chartDataCopy: any) {
    this.propertyValueMap.type = this.widgetScope.type;
    this.chart = this.chartService.initChart(this.widgetScope, this.xDomainValues, this.yDomainValues, this.propertyValueMap, !this.chartService.isBubbleChart(this.widgetScope.type));
    d3.select(this.chartId + ' svg')
      .datum(chartDataCopy)
      .call(this.chart);
    this.chartService.postPlotChartProcess(this.propertyValueMap, true, this.chart);
    return this.chart;
  }

  showFormattingOptions(formatType: string) {
    //Hide or show format options based on x column type
    let xAxisDataType =  this.getDataType(this.widgetScope, formatType),
      showDate      = false,
      showNumber    = false;

    if (isDateTimeType(xAxisDataType)) {
      showDate = true;
    } else if (isNumberType(xAxisDataType) || (!this.widgetScope.binddataset && this.widgetScope.type === 'Bubble')) {
      showNumber = true;
    }
    this.chartService.hideOrShowProperties(['xdateformat'], this, showDate, 'showindesigner');
    this.chartService.hideOrShowProperties(['xnumberformat'], this, showNumber, 'showindesigner');
  }

  getDataType(widgetScope: any, formatType: string) {
    return !widgetScope.isLiveVariable ? formatType : this.typeService.getTypeForExpression(widgetScope.binddataset + '.' + widgetScope.xaxisdatakey);
  }

  shadowPropertyChange(property: any, value: any) {
    let oldValue = this.propertyValueMap[property];
    this.propertyValueMap[property] = value;
    switch (property) {
      case 'showlegend':
        if (oldValue === "bottom" || oldValue === "right" && value === "top") {
          //Replot the chart in this case
          this.plotChartProxy();
        } else {
          this.handleLegend(value, oldValue);
        }
        break;
      case 'showxaxis':
      case 'showyaxis':
        this.chartService.toggleAxisShow(property, value);
        break;
      case 'offsettop':
      case 'offsetright':
      case 'offsetbottom':
      case 'offsetleft':
        //Converting strings to numbers
        this.propertyValueMap[property] = Number(value);
        break;
    }
    //Plot the chart for above property change
    if (includes(this.basicProperties, property)) {
      this.plotChartProxy();
    } else {
      this.propertyChangeHandler(property, value);
    }
    this.liveWidgetSettingsService.shadowPropertyChange(property, this.propertyValueMap[property]);
  }

  propertyChangeHandler(key: any, newVal: any) {
    switch (key) {
      case 'linethickness':
        this.chartService.setLineThickness(null, newVal);
        break;
      case 'highlightpoints':
        this.chartService.highlightPoints(null, newVal);
        break;
      case 'formattype':
        this.showFormattingOptions(newVal);
        this.clearFormatOptions();
        break;
    }
  }

  clearFormatOptions() {
    this.liveWidgetSettingsService.shadowPropertyChange('xnumberformat', '');
    this.liveWidgetSettingsService.shadowPropertyChange('xdateformat', '');
    this.propertyValueMap.xnumberformat = '';
    this.propertyValueMap.xdateformat = '';
  }

  handleLegend(newVal: any, oldVal: any) {
    switch (newVal) {
      case 'hide':
        this.plotChartProxy();
        break;
      case 'top':
      case 'bottom':
        //Plot the chart to show legend
        if (oldVal === 'hide' || oldVal === 'right' || !oldVal) {
          this.plotChartProxy(false, newVal);
        } else {
          if (!(isEmpty(this.chart))) {
            this.chartService.modifyLegendPosition(this.widgetScope, newVal, this.chart);
          }
        }
        break;
      case 'right':
        this.plotChartProxy(true, newVal);
        break;
    }
  }

  prepareLegendPositionOptions(isPieType: boolean) {
    let options = this.widgetProps.showlegend.options,
      pieTypeOptions = ["right"];
    if (isPieType) {
      this.widgetProps.showlegend.options = includes(options, pieTypeOptions[0]) ? options : options.concat(pieTypeOptions);
    } else {
      remove(this.widgetProps.showlegend.options, (x: any) => {return pieTypeOptions.indexOf(x) !== -1});
    }
  }
}
